<template>
    <div class="datagrid-wrapper">
        <div id="users-overview">
            <div class="row">
                <div class="col-md-12 title-holder">
                    <h1>{{ table_title }}</h1>
                    <div class="grid-buttons" data-type="buttons">
                        <button v-if="show_create_user" id="start-create-user" class="btn btn-light" href="#">Create User</button>

                        <a id="proton-sync-contacts-placeholder" class="d-none"></a>
                        <manual-sync-trigger-button :centre="centre_id" sync-type='contacts'></manual-sync-trigger-button>
                    </div>
                    <div class="select-wrapper float-start">
                      <select class="form-control" v-model="selected" @change="filterRoles">
                        <option v-for="(item, key) in filter_roles" :value="key">
                          {{item}}
                        </option>
                      </select>
                    </div>
                    <div class="search-form-mask open">
                        <form role="search" method="get" class="form-inline search-form">
                            <div class="form-group">
                                <input v-model.lazy="search" type="search" class="sb-search-input form-control"
                                       placeholder="Search for.."/>
                            </div>
                            <div v-if="search">
                                <button @click.prevent="reset" type="reset" class="btn-icon btn-light sb-icon-reset">
                                <span class="fa fa-close">
                                    <span class="visually-hidden">Reset</span>
                                </span>
                                </button>
                            </div>
                            <button @click.prevent="getUsers" type="submit" class="btn btn-light sb-icon-search">
                            <span class="fa fa-search">
                                <span class="visually-hidden">Search</span>
                            </span>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-md-12">
                <table class="datagrid table">
                    <thead>
                    <tr>
                        <th v-for="header in table_headers">
                            <div v-if="header === 'name'">
                                <button type="button" class="btn btn-link"  @click="sortBy('users.full_name')">{{ header }} &#8597;</button>
                            </div>
                            <div v-else>
                                <span class="btn-link">
                                    {{ header }}
                                    <button
                                        v-if="header === 'tag status'"
                                        type="button"
                                        class="btn btn-help-icon"
                                        data-bs-toggle="tooltip"
                                        data-bs-html="true"
                                        :title="status_tooltip"
                                        data-bs-container="body"
                                        data-bs-placement="right"
                                        data-bs-custom-class="tooltip-text-start"
                                    >?</button>
                                </span>
                            </div>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="!users.data || !users.data.length">
                        <td :colspan="table_headers.length">
                            <div v-if="isLoading">
                                <span class="spinner visible anim-spin"></span>
                            </div>
                            <div v-else>
                                No users found
                            </div>
                        </td>
                    </tr>
                    <tr v-for="user in users.data" v-bind:key="user.id">
                        <td v-for="column in table_columns">
                            <div v-if="column === 'name'">
                                <a :href="`/employees/${user.id}`">{{ user.name }}</a>
                            </div>
                            <div v-else-if="column === 'roles'">
                                <span v-for="role in user.roles.join(', ')" class="">{{ role }}</span>
                            </div>
                            <div v-else-if="column === 'status'">
                                <span :class="{
                                    'text-active': user.status === 'active',
                                    'text-danger': user.status === 'blocked',
                                    'text-inactive': user.status === 'inactive'
                                }">{{user.status}}</span>
                            </div>
                            <div v-else-if="column === 'tag_numbers'">
                                <span v-if="user.tag_numbers.length === 0" class="text-inactive">none</span>
                                <span v-else v-for="tag in user.tag_numbers">{{tag}}<br/></span>
                            </div>
                            <div v-else-if="column === 'tag_ids'">
                                <div v-if="user.tag_ids.length === 0"></div>
                                <div v-else v-for="tag_id in user.tag_ids">
                                    <span class="spinner visible anim-spin"></span>
                                    <span data-clp="status" data-type="tag" :data-value="tag_id"></span><br/>
                                </div>
                            </div>
                            <div v-else-if="column === 'companies'" v-for="company in user.companies">
                                <span v-for="(name, id) in company">
                                    <a :href="`/companies/${id}`">{{ name }}</a>
                                </span>
                                <br/>
                            </div>
                            <div v-else-if="column === 'action'">
                                <div v-if="user.show_spinner">
                                    <span class="spinner anim-spin"></span>
                                </div>
                                <div v-else-if="user.show_block_and_assign">
                                    <span v-if="can_block_user">
                                        <a @click="blockUser(user)"  :href="`/centre/users/${user.id}/blockuser`"
                                           :data-type-action="user.block_value" :class="user.block_value + '-user user-overview-block btn btn-link link-button'">
                                            {{ user.block_text }}
                                        </a>
                                    </span>
                                    <span v-if="user.tags_count === 0 && can_assign_tags">
                                        &nbsp;&dash;&nbsp;
                                        <a :data-user-id="user.id" :href="getAssignTagLink(centre_is_nebula_enabled, centre_id, user.id)"
                                           class="assign-tags-user btn btn-link link-button">Assign tag</a>
                                    </span>
                                    <span v-if="show_button_to_view_xs4_com_ids"> - <a class="js-show-xs4-com-ids-for-user btn btn-link link-button" :data-href="`/employees/${user.id}/show-xs4-com-id`">Show XS4 Com id</a></span>
                                </div>
                                <div v-else>Company blocked</div>
                            </div>
                            <div v-else v-html="user[column]"></div>
                        </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                        <td colspan="3">
                            <pagination :data="users" :limit=3 @pagination-change-page="getUsers">
                                <template #prev-nav>
                                    <span>&lt; Previous</span>
                                </template>
                                <template #next-nav>
                                    <span>Next &gt;</span>
                                </template>
                            </pagination>
                        </td>
                    </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>
<script setup>
import manualSyncTriggerButton from "../Sync/ManualSyncTriggerButton.vue";
import {onMounted, ref, watch} from "vue";
import clp from "@/_clp.js";

const props = defineProps({
    centre_id: Number,
    api_endpoint: String,
    table_title: String,
    show_create_user: Boolean,
    can_block_user: Boolean,
    can_assign_tags: Boolean,
    table_headers: Array,
    table_columns: Array,
    filter_roles: Object,
    centre_is_nebula_enabled: Boolean,
    status_tooltip: String,
    show_button_to_view_xs4_com_ids: Boolean,
});

const selected = ref('');
const users = ref({});
const paginate = ref(10);
const search = ref('');
const sortKey = ref('');
const reverse = ref(false);
const roleFilter = ref('');
const isLoading = ref(false);
const currentPage = ref(1);

watch(paginate, () => {
    getUsers();
});

const filterRoles = () => {
    roleFilter.value = selected.value;
    getUsers();
};

const sortBy = (key) => {
    reverse.value = (sortKey.value === key) ? !reverse.value : false;
    sortKey.value = key;
    getUsers();
};

const getUsers = (page = 1) => {
    isLoading.value = true;
    refreshHardware();
    axios.get(props.api_endpoint, {
        params: {
            page,
            paginate: paginate.value,
            q: search.value,
            sort_key: sortKey.value,
            sort_direction: reverse.value ? 'DESC' : 'ASC',
            role_filter: roleFilter.value ? [roleFilter.value] : [],
        }
    })
        .then(response => {
            users.value = response.data;
            currentPage.value = response.data.meta.current_page;
            isLoading.value = false;
        });
};

const reset = () => {
    search.value = '';
    getUsers();
};

const refreshHardware = () => {
    // needed for /helper/get_clp_status_for_tags call
    setTimeout(function() {
        clp.refreshHardware($(document));
    }, 2000);
};

const blockUser = (user) => {
    user.show_spinner = true;
    setTimeout(() => {
        user.show_spinner = false;
        user.block_text = user.is_blocked ? 'Block user' : 'Unblock user';
        getUsers(currentPage.value);
    }, 2000);
};

const getAssignTagLink = (centre_is_nebula_enabled, centre_id, user_id) => {
    if (centre_is_nebula_enabled) {
        return `/centre/${centre_id}/user/${user_id}/assign_card`;
    }
    return `/centre/users/${user_id}/assigntag`;
};

onMounted(() => {
    getUsers();
});
</script>
<style scoped>
.btn-link,.btn-link:hover {
    letter-spacing: normal;
    padding: 0 30px 0 0;
    margin: 0;
    vertical-align: bottom;
    color: #babfbf;
    font-weight: bold;
    font-size: 11px;
    outline: none !important;
    border: 0;
    text-decoration: none;
    white-space: nowrap;
}
.select-wrapper, .sync-btn-container {
    margin-left: 10px;
}
</style>
